import { LogSearchParam } from '@@types/Api/Log';
import Channel from '@@types/Channel';
import { LOG_ID, StringTMap } from '@@types/common';
import Log, { AllSearchLog } from '@@types/Log';

import { ChannelType, SEARCH_LOG_TYPE } from '~/constants';
import { channelsStore } from '~/stores/ChannelsStore';
import { threadChannelStore } from '~/stores/ThreadChannelStore';
import { subtractFromNow } from '~/utils/dateUtil';
import { keyBy } from '~/utils/fnUtil/fnUtil';
import { isEmpty } from 'lodash';
import { action, computed, observable } from 'mobx';
import { all, any, equals, find, map, mapObjIndexed, propEq, reject } from 'ramda';
import trim from 'ramda/es/trim';

class SearchStore {
  selectAllAfterFetch = false;

  @observable channelId = 'all';

  @observable fileType = 'all';

  @observable from = 0; // page

  @observable isSearching = false;

  @observable searchKeyword = '';

  @computed
  get trimedSearchKeyword() {
    return trim(this.searchKeyword);
  }

  @observable memberId = 'all';

  @observable mentionType = 'all';

  @observable searchedKeyword = '';

  @observable searchedLogIds: LOG_ID[] = [];

  @observable searchedLogMap: StringTMap<Log | AllSearchLog> = {};

  @observable.shallow searchedCheckedMap: StringTMap<boolean> = {};

  @observable totalCount = -1;

  @observable searchedMembers = [];

  @observable searchedFor = null;

  @observable startDate = subtractFromNow(1, 'year');

  @observable endDate = null;

  @observable searchType: SEARCH_LOG_TYPE = SEARCH_LOG_TYPE.ALL;

  searchParams: LogSearchParam | null = null;

  @computed get searchedLogs(): Log[] | AllSearchLog[] {
    return this.searchedLogIds.map((id) => this.searchedLogMap[id]) as Log[] | AllSearchLog[];
  }

  @computed get allChecked(): boolean {
    return all((value) => !!value, Object.values(this.searchedCheckedMap));
  }

  @computed get hasChecked(): boolean {
    return any((value) => !!value, Object.values(this.searchedCheckedMap));
  }

  @action
  setSearchParams = (params: LogSearchParam | null) => {
    this.searchParams = params;
  };

  @action
  setSearchedFor = (type) => {
    this.searchedFor = type;
  };

  @action
  setSearchedMember = (members) => {
    this.searchedMembers = members;
    return members;
  };

  @action
  setTotalCount = (count) => {
    this.totalCount = count;
  };

  @action
  clear = () => {
    this.setChannelId('all');
    this.setFileType('all');
    this.setFrom(0);
    this.setIsSearching(false);
    this.setSearchKeyword('');
    this.setMemberId('all');
    this.setMentionType('all');
    this.setSearchedKeyword('');
    this.resetData();
    this.setStartDate(subtractFromNow(1, 'year'));
    this.setEndDate(null);
    this.setSearchParams(null);
    this.setSearchType(SEARCH_LOG_TYPE.ALL);
  };

  @action
  setChannelId = (channelId) => {
    this.channelId = channelId;
  };

  @action
  setFileType = (fileType) => {
    this.fileType = fileType;
  };

  @action
  setFrom = (from) => {
    this.from = from;
  };

  @action
  setIsSearching = (isSearching) => {
    this.isSearching = isSearching;
  };

  @action
  setSearchKeyword = (keyword) => {
    this.searchKeyword = keyword;
  };

  @action
  setMemberId = (memberId) => {
    this.memberId = memberId;
  };

  @action
  setMentionType = (mentionType) => {
    this.mentionType = mentionType;
  };

  @action
  setSearchedKeyword = (keyword) => {
    this.searchedKeyword = keyword;
  };

  public resetData = () => {
    this.setSearchedLogs({ contents: [], totalCount: -1, isReset: true });
  };

  @action
  setSearchedLogs = ({
    contents,
    totalCount,
    references,
    isReset = false,
  }: {
    contents: Log[] | AllSearchLog[];
    totalCount: number;
    references?: { channelMap: StringTMap<Channel> };
    isReset?: boolean;
  }) => {
    this.searchedLogMap = keyBy('id', contents);
    this.searchedLogIds = map((log: Log | AllSearchLog) => log.id, contents);
    this.searchedCheckedMap = mapObjIndexed(() => this.selectAllAfterFetch, this.searchedLogMap);
    if (!isReset) {
      this.selectAllAfterFetch = false;
    }
    if (references) {
      Object.values(references.channelMap).forEach((channel: Channel) => {
        if (channel.type === ChannelType.Thread) {
          threadChannelStore.updateChannel(channel);
        } else {
          channelsStore.updateChannel(channel);
        }
      });
    }
    this.setTotalCount(totalCount);
    return this.searchedLogs;
  };

  @action
  handleClickCheckbox = (logId: LOG_ID, value: boolean) => {
    this.searchedCheckedMap[logId] = value;
  };

  @action
  handleClickAllCheckbox = (value: boolean) => {
    this.searchedCheckedMap = mapObjIndexed(() => value, this.searchedCheckedMap);
  };

  getSearchedMemberById = (id) => {
    return find(propEq('id', id), this.searchedMembers);
  };

  @action
  setStartDate = (startDate) => {
    this.startDate = startDate;
  };

  @action
  setEndDate = (endDate) => {
    this.endDate = endDate;
  };

  @action
  deleteSearchedLog = (logId: LOG_ID) => {
    if (isEmpty(this.searchedLogMap)) {
      return;
    }
    delete this.searchedLogMap[logId];
    delete this.searchedCheckedMap[logId];
    this.searchedLogIds = reject(equals(logId), this.searchedLogIds);
  };

  @action
  setSearchType = (searchType: SEARCH_LOG_TYPE) => {
    this.searchType = searchType;
  };
}

export default SearchStore;

export const searchStore = new SearchStore();
